import React from 'react'
import { BrowserRouter } from 'react-router-dom'
import { hydrate, render } from 'react-dom'
import { Provider } from 'react-redux'
import { init as SentryInit, ErrorBoundary as SentryErrorBoundary } from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing/esm/browser'
import { CaptureConsole } from '@sentry/integrations'
import { CookiesProvider } from 'react-cookie'
import { HelmetProvider } from 'react-helmet-async'
import { loadableReady } from '@loadable/component'
import { datadogRum } from '@datadog/browser-rum'

import { App } from '@app/app'
import { createStore } from '@app/store/store'
import { runtimeConfig } from '@app/common/config'
import { ScrollToTop } from '@app/utils/scrollToTop'

import { registerServiceWorker } from './serviceWorker'

if (runtimeConfig.joinSproutTherapyConfig?.SENTRY_DSN) {
  SentryInit({
    dsn: runtimeConfig.joinSproutTherapyConfig?.SENTRY_DSN,
    integrations: [new BrowserTracing(), new CaptureConsole({ levels: ['error'] })],
    environment: runtimeConfig.joinSproutTherapyConfig?.ENV_NAME,
    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 1.0
  })
}

if (
  runtimeConfig.joinSproutTherapyConfig?.DATADOG_APPLICATION_ID &&
  runtimeConfig.joinSproutTherapyConfig?.DATADOG_CLIENT_TOKEN
) {
  datadogRum.init({
    applicationId: runtimeConfig.joinSproutTherapyConfig.DATADOG_APPLICATION_ID,
    clientToken: runtimeConfig.joinSproutTherapyConfig.DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: 'elemy.com',
    sampleRate: 100,
    trackInteractions: true,
    defaultPrivacyLevel: 'mask-user-input'
  })
}

const initialState = window.__INITIAL_STATE__
const renderMethod = window.__RENDER_METHOD__ === 'render' ? render : hydrate
const store = createStore(initialState)

registerServiceWorker()

loadableReady(() => {
  renderMethod(
    <SentryErrorBoundary>
      <Provider store={store}>
        <BrowserRouter>
          <ScrollToTop />
          <CookiesProvider>
            <HelmetProvider>
              <App />
            </HelmetProvider>
          </CookiesProvider>
        </BrowserRouter>
      </Provider>
    </SentryErrorBoundary>,
    document.getElementById('root'),
    () => delete window.__INITIAL_STATE__
  )
})
